import React from "react";
import { PhoneIcon, MessageSquareIcon, MailIcon, ExternalLinkIcon, UserIcon } from "lucide-react";
import { Badge } from "./primitives";
import { borrower, latestLoan } from "../data/contact";
function HeaderMeta({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex items-center gap-2" data-id="element-218" data-mp="qlqyzmw948q4">
      <span className="text-slate-400" data-id="element-219" data-mp="3p469kw2acdt4">{icon}</span>
      <div className="leading-tight" data-id="element-220" data-mp="3p5hex0bdptmc">
        <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400" data-id="element-221" data-mp="3u2zfo3f8t2j9">
          {label}
        </p>
        <p className="text-sm font-medium text-slate-800" data-id="element-222" data-mp="2w6c4swz8rbk">{value}</p>
      </div>
    </div>;
}
function ActionButton({
  icon,
  label,
  primary = false,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  primary?: boolean;
  onClick?: () => void;
}) {
  return <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${primary ? "bg-[#1a73e8] text-white hover:bg-blue-700" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`} data-id="element-223" data-mp="3bchzeybljzes">
      {icon}
      <span className="hidden sm:inline" data-id="element-224" data-mp="16qf8f7s3uv9a">{label}</span>
    </button>;
}
export function SummaryHeader({
  onCreateLoan
}: {
  onCreateLoan: () => void;
}) {
  const fullName = `${borrower.firstName} ${borrower.lastName}`;
  const initials = `${borrower.firstName[0]}${borrower.lastName[0]}`;
  return <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur" data-id="element-225" data-mp="ntxh2rh64xdk">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6" data-id="element-226" data-mp="nvqmy4jyjdki">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-227" data-mp="jtwrqbsj71rq">
          <div className="flex items-start gap-4" data-id="element-228" data-mp="2i6ndpgyx8kon">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-50 text-base font-semibold text-[#1a73e8] ring-1 ring-blue-100" data-id="element-229" data-mp="2e6d3tt80da4i">
              {initials}
            </div>
            <div data-id="element-230" data-mp="ga4rpn2kokd4">
              <div className="flex flex-wrap items-center gap-2" data-id="element-231" data-mp="gaoat73ylesq">
                <h1 className="text-lg font-semibold text-slate-900" data-id="element-232" data-mp="2izz34xmgsca">
                  {fullName}
                </h1>
                <Badge tone="slate" icon={<UserIcon className="h-3 w-3" data-id="element-234" data-mp="6lef7h9wpdzv" />} data-id="element-233" data-mp="6k3clks76cac">
                  Contact
                </Badge>
                {latestLoan.applicationTaken && <Badge tone="blue" data-id="element-235" data-mp="z9a6u7kmcfps">Application Taken</Badge>}
                {latestLoan.isRefinance && <Badge tone="green" data-id="element-236" data-mp="l9rcqion74wh">Refinance</Badge>}
                {latestLoan.isCashOut && <Badge tone="amber" data-id="element-237" data-mp="q8c4rutqqi9u">Cash Out Refinance</Badge>}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3" data-id="element-238" data-mp="35zzwnbtwdkls">
                <HeaderMeta icon={<PhoneIcon className="h-4 w-4" data-id="element-240" data-mp="3az33n1tfu0we" />} label="Primary Phone" value={borrower.phone} data-id="element-239" data-mp="3622vmld12o2n" />
                <HeaderMeta icon={<MailIcon className="h-4 w-4" data-id="element-242" data-mp="2ygqp7o4ybvzd" />} label="Email" value={borrower.email} data-id="element-241" data-mp="32jvolhtqjhjs" />
                <HeaderMeta icon={<UserIcon className="h-4 w-4" data-id="element-244" data-mp="3cdm96bmqr7u7" />} label="Lead Source" value={borrower.leadSource} data-id="element-243" data-mp="3cfgmb3rrtvnq" />
                <HeaderMeta icon={<ExternalLinkIcon className="h-4 w-4" data-id="element-246" data-mp="2jp89lrg083gj" />} label="Latest Loan Status" value={latestLoan.status} data-id="element-245" data-mp="2js2wdpmhq0d4" />
                <HeaderMeta icon={<UserIcon className="h-4 w-4" data-id="element-248" data-mp="2tixk6yfdx80o" />} label="Loan Purpose" value={latestLoan.loanPurpose} data-id="element-247" data-mp="2thw6snawc9fs" />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-id="element-249" data-mp="20ygyolgdc501">
            <ActionButton icon={<UserIcon className="h-4 w-4" data-id="element-251" data-mp="1vzw3j0fz4big" />} label="Create New Loan" primary onClick={onCreateLoan} data-id="element-250" data-mp="1w17995szbtc7" />
            <ActionButton icon={<PhoneIcon className="h-4 w-4" data-id="element-253" data-mp="149ofkzqrd9v7" />} label="Call" data-id="element-252" data-mp="14a81ssnfykf1" />
            <ActionButton icon={<MessageSquareIcon className="h-4 w-4" data-id="element-255" data-mp="18a8g8dajivm3" />} label="Text" data-id="element-254" data-mp="18arzbxbxfq1p" />
            <ActionButton icon={<MailIcon className="h-4 w-4" data-id="element-257" data-mp="1d2rz9przyiya" />} label="Email" data-id="element-256" data-mp="1d29u23ncwue8" />
            <ActionButton icon={<ExternalLinkIcon className="h-4 w-4" data-id="element-259" data-mp="1hzrz3iy96pgy" />} label="Open Latest Loan" data-id="element-258" data-mp="1i13bu89of7k7" />
          </div>
        </div>
      </div>
    </header>;
}